import type { FirebaseWebConfig } from '@/api/types';
import type { FirebaseAuthProvider } from '@/features/auth/firebaseAuthProvider';
import type { Auth } from 'firebase/auth';

/**
 * The concrete `firebase/auth` adapter behind the `FirebaseAuthProvider` seam —
 * the sole module that touches the `firebase` SDK. It drives the interactive
 * `/signin` flows (email/password + the Google popup) and hands back a Firebase
 * ID token for `authStore.signInWithFirebaseToken` to exchange for a
 * `nori_session` (the client mirror of the server's ID-token verifier). It is a
 * pure token-minter: once the token is exchanged for a session cookie the
 * identity rides that cookie, so — unlike the reference app — there is no
 * ongoing auth-state listener or token-refresh timer here.
 *
 * The `firebase` SDK is imported dynamically (only its TYPES are imported at the
 * top, and those are erased), so it is code-split into a chunk fetched on the
 * first sign-in rather than shipped in the eager bundle — a browsing session
 * that never signs in pays nothing for the SDK.
 *
 * The public Firebase Web config is built into the client (`FIREBASE_WEB_CONFIG`
 * in `firebaseConfig.ts`), so the adapter takes a `getConfig` reader — wired to
 * that constant in `main.ts` — and initializes the SDK lazily, once, on the
 * first sign-in.
 */

// Firebase raises one of these when the *user* dismisses the Google popup
// (closed it, declined consent, or opened a second popup that supersedes this
// one). An aborted sign-in is a silent no-op per the seam contract, not a
// failure to surface. `auth/popup-blocked` is deliberately absent — that is a
// real environmental failure worth showing.
const ABORT_CODES = new Set<string>([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

// Firebase collapses a wrong password and an unknown account into
// `auth/invalid-credential` when email-enumeration protection is on (the modern
// default), and reports the older split codes when it is off. All three mean the
// same thing to a signing-in user.
const BAD_CREDENTIAL_CODES = new Set<string>([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
]);

function errorCode(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

export function createFirebaseAuthAdapter(
  getConfig: () => FirebaseWebConfig | null,
): FirebaseAuthProvider {
  // Memoize the initialized `Auth` so the SDK is set up exactly once. A failed
  // init (e.g. the config was not yet available) resets the memo so a later
  // sign-in can retry rather than being wedged on a poisoned promise.
  let authPromise: Promise<Auth> | null = null;

  function ensureAuth(): Promise<Auth> {
    if (authPromise === null) {
      authPromise = initAuth().catch((error: unknown) => {
        authPromise = null;
        throw error;
      });
    }
    return authPromise;
  }

  async function initAuth(): Promise<Auth> {
    const config = getConfig();
    if (config === null) {
      throw new Error('Sign-in is not configured on this registrar.');
    }
    const { getApp, getApps, initializeApp } = await import('firebase/app');
    const {
      initializeAuth,
      indexedDBLocalPersistence,
      browserLocalPersistence,
      browserPopupRedirectResolver,
    } = await import('firebase/auth');
    // Reuse the default app if one already exists so a re-init never trips
    // `app/duplicate-app` (which would mask the real error).
    const app = getApps().length > 0 ? getApp() : initializeApp(config);
    return initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  }

  return {
    async signInWithGoogle(): Promise<string | null> {
      const { GoogleAuthProvider, signInWithPopup } = await import(
        'firebase/auth'
      );
      const provider = new GoogleAuthProvider();
      try {
        const credential = await signInWithPopup(await ensureAuth(), provider);
        return await credential.user.getIdToken();
      } catch (error) {
        const code = errorCode(error);
        if (code !== undefined && ABORT_CODES.has(code)) {
          return null;
        }
        if (code === 'auth/account-exists-with-different-credential') {
          throw new Error(
            'An account with this email already exists. Please sign in with your password.',
          );
        }
        throw error;
      }
    },

    async signInWithEmail(email: string, password: string): Promise<string | null> {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      try {
        const credential = await signInWithEmailAndPassword(
          await ensureAuth(),
          email,
          password,
        );
        return await credential.user.getIdToken();
      } catch (error) {
        const code = errorCode(error);
        if (code !== undefined && BAD_CREDENTIAL_CODES.has(code)) {
          throw new Error('Incorrect email or password.');
        }
        throw error;
      }
    },

    async signOut(): Promise<void> {
      // Only clear a session this session actually established. If the SDK was
      // never initialized (no Firebase sign-in happened), there is nothing to
      // clear and we must NOT `import('firebase/auth')` just to no-op — that
      // would pull the code-split SDK onto the common logout path.
      if (authPromise === null) {
        return;
      }
      const { signOut: firebaseSignOut } = await import('firebase/auth');
      await firebaseSignOut(await ensureAuth());
    },
  };
}
